import { Service } from '@genial/services';
import { EquityEnumsSubscriptionPeriodEnum } from '@genial/services/dist/models';
import { selector, selectorFamily } from 'recoil';

import { FiltersToApplyProps } from '../../hooks/useFilterTransformer';
import { advisorIdToApisSelector, partnerIdToApisSelector } from '../master/selector';
import { DynamicMarketStatusProps } from '../product/types';

import { screenName } from './constants';
import {
  normalizeSubscriptionCustomersList,
  normalizeSubscriptionHistoryList,
  normalizeSubscriptionList,
} from './normalizer';
import {
  SubscriptionCustomerItemProps,
  SubscriptionHistoryItemProps,
  SubscriptionItemProps,
} from './types';

export const subscriptionListQuery = selectorFamily<
  | {
      data: SubscriptionItemProps[];
    }
  | undefined,
  { filtersToApply: FiltersToApplyProps }
>({
  key: 'subscription/subscriptionListQuery',
  get: ({ filtersToApply }) => {
    return async ({ get }) => {
      const advisorSelected = get(advisorIdToApisSelector);
      const partnerSelected = get(partnerIdToApisSelector);

      const { data } = await Service.Equities.V1.getSubscriptionVoluntaryEvents({
        screenName,
        advisorId: advisorSelected,
        partnerId: partnerSelected,
        filterType: (filtersToApply.filterType as EquityEnumsSubscriptionPeriodEnum) || 'Month',
        ...filtersToApply,
      });

      const normalizedData = normalizeSubscriptionList(data);

      return { data: normalizedData };
    };
  },
});

export const subscriptionCustomersAvailableQuery = selectorFamily<
  | {
      data: SubscriptionCustomerItemProps[];
    }
  | undefined,
  { eventId: number }
>({
  key: 'subscription/subscriptionCustomersAvailableQuery',
  get: ({ eventId }) => {
    return async ({ get }) => {
      const advisorSelected = get(advisorIdToApisSelector);
      const partnerSelected = get(partnerIdToApisSelector);

      const { data } = await Service.Equities.V1.getSubscriptionByVoluntaryEventsEventIdSubscribers(
        {
          screenName,
          eventId,
          advisorId: advisorSelected,
          partnerId: partnerSelected,
        }
      );

      const normalizedData = normalizeSubscriptionCustomersList(data);

      return { data: normalizedData };
    };
  },
});

export const subscriptionCustomersHistoryQuery = selectorFamily<
  | {
      data: SubscriptionHistoryItemProps[];
    }
  | undefined,
  { baseIsinCode: string }
>({
  key: 'subscription/subscriptionCustomersHistoryQuery',
  get: ({ baseIsinCode }) => {
    return async ({ get }) => {
      const advisorSelected = get(advisorIdToApisSelector);
      const partnerSelected = get(partnerIdToApisSelector);

      const { data } = await Service.Equities.V1.getSubscriptionMovements({
        screenName,
        baseIsinCode,
        advisorId: advisorSelected,
        partnerId: partnerSelected,
      });

      const normalizedData = normalizeSubscriptionHistoryList(data);

      return { data: normalizedData };
    };
  },
});

export const subscriptionMarketStatusQuery = selector<DynamicMarketStatusProps>({
  key: 'subscription/subscriptionMarketStatusQuery',
  get: async () => {
    const { data } = await Service.Equities.V1.getMarketStatusSubscription({
      screenName,
    });

    return {
      isOpen: data?.market.isOpened,
      message: data?.market.status || '',
      originalData: data,
    };
  },
});
